/**
 * Hook that returns event handlers and styles for playful interactions.
 *
 * Spread the returned object onto any element to enable playful effects:
 * ```tsx
 * const playful = usePlayfulHandlers();
 * return <div {...playful}>...</div>;
 * ```
 */

import { useState, useCallback, useMemo, CSSProperties } from 'react';
import { usePlayful } from './usePlayful';
import { useParticles } from './useParticles';
import { useSoundPlayer } from './useSoundPlayer';
import { AnimationBehavior } from './types';

interface PlayfulHandlerOptions {
  /** Which interactions to enable (default: all) */
  interactions?: ('hover' | 'click' | 'idle')[];
  /** Disable all effects (e.g., for frozen cards) */
  disabled?: boolean;
}

interface PlayfulHandlerReturn {
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (e: React.MouseEvent) => void;
  style: CSSProperties;
  className: string;
}

function animationToStyle(behavior: AnimationBehavior): CSSProperties {
  const style: CSSProperties = {};
  if (behavior.transform) style.transform = behavior.transform;
  if (behavior.transition) style.transition = behavior.transition;
  if (behavior.animationName) {
    style.animationName = behavior.animationName;
    style.animationDuration = behavior.animationDuration;
    style.animationTimingFunction = behavior.animationTimingFunction;
    style.animationIterationCount = behavior.animationIterationCount;
  }
  return style;
}

export function usePlayfulHandlers(
  options: PlayfulHandlerOptions = {}
): PlayfulHandlerReturn {
  const { interactions = ['hover', 'click', 'idle'], disabled = false } =
    options;
  const { themeDefinition, soundEnabled } = usePlayful();
  const { burst } = useParticles();
  const { play } = useSoundPlayer();
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const enableHover = interactions.includes('hover');
  const enableClick = interactions.includes('click');
  const enableIdle = interactions.includes('idle');

  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !enableHover) return;
      setIsHovered(true);
      if (soundEnabled && themeDefinition.sounds?.hover) {
        play(themeDefinition.sounds.hover);
      }
      // Small particle burst on hover
      if (themeDefinition.particles.enabled) {
        const rect = e.currentTarget.getBoundingClientRect();
        burst(rect.left + rect.width / 2, rect.top, 3);
      }
    },
    [disabled, enableHover, soundEnabled, themeDefinition, play, burst]
  );

  const onMouseLeave = useCallback(() => {
    if (disabled) return;
    setIsHovered(false);
    setIsClicked(false);
  }, [disabled]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !enableClick) return;
      setIsClicked(true);
      if (soundEnabled && themeDefinition.sounds?.click) {
        play(themeDefinition.sounds.click);
      }
      if (themeDefinition.particles.enabled) {
        burst(e.clientX, e.clientY);
      }
      // Reset click state after animation
      setTimeout(() => setIsClicked(false), 200);
    },
    [disabled, enableClick, soundEnabled, themeDefinition, play, burst]
  );

  const style = useMemo<CSSProperties>(() => {
    if (disabled) return {};

    // Priority: click > hover > idle
    if (isClicked && enableClick) {
      return animationToStyle(themeDefinition.click);
    }
    if (isHovered && enableHover) {
      return animationToStyle(themeDefinition.hover);
    }
    if (enableIdle) {
      return animationToStyle(themeDefinition.idle);
    }
    return {};
  }, [
    disabled,
    isClicked,
    isHovered,
    enableClick,
    enableHover,
    enableIdle,
    themeDefinition,
  ]);

  return {
    onMouseEnter,
    onMouseLeave,
    onClick,
    style,
    className: '',
  };
}
